import React, { useState } from "react";
import { Box, Grid, Divider } from "@mui/material";
import { makeStyles } from "@mui/styles";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import InputField from "../../../../components/InputFields/InputField";
import { Button } from "../../../../components/Buttons";
import { GREY, BRAND, BASE, BORDER } from "../../../../utils/constants/colors";
import { fontWeight } from "../../../../utils/constants/UI";
import CustomDatePicker from "../../../../components/DatePicker/CustomDatePicker";
import dayjs from "dayjs";

const useStyles = makeStyles({
  container: {
    padding: "12px 16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "0px",
    borderLeft: `1px solid ${BORDER.DEFAULT}`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    marginBottom: "12px",
    paddingBottom: "8px",
    borderBottom: `1px solid ${GREY.W_100}`,
  },
  formContent: {
    flex: 1,
    overflowY: "auto",
    paddingRight: "4px",
    "&::-webkit-scrollbar": {
      width: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: GREY.W_100,
      borderRadius: "10px",
    },
  },
  section: {
    marginBottom: "12px",
  },
  sectionTitle: {
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  field: {
    marginBottom: "8px",
  },
  label: {
    marginBottom: "2px",
  },
  footer: {
    marginTop: "auto",
    paddingTop: "12px",
    borderTop: `1px solid ${GREY.W_100}`,
  },
  gridContainer: {
    display: "flex",
    gap: "8px",
  },
});

const ConsolidatedReportLog = ({ onSave, isLoading, editData, onCancel }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    date: dayjs(),
    // Calls
    answered_calls: "",
    un_ans_calls: "",
    // Emails
    num_of_email_sent: "",
    // Responses
    num_of_res: "",
    // LinkedIn
    connection_linkedIn: "",
    sent_msg_linkedIn: "",
    respond_linkedIn: "",
  });

  React.useEffect(() => {
    if (editData) {
      setFormData({
        date: dayjs(editData.date),
        answered_calls: editData.calls?.answered_calls || "",
        un_ans_calls: editData.calls?.un_ans_calls || "",
        num_of_email_sent: editData.emails?.num_of_email_sent || "",
        num_of_res: editData.responses?.num_of_res || "",
        connection_linkedIn: editData.linkedIn?.connection_linkedIn || "",
        sent_msg_linkedIn: editData.linkedIn?.sent_msg_linkedIn || "",
        respond_linkedIn: editData.linkedIn?.respond_linkedIn || "",
      });
    } else {
      setFormData({
        date: dayjs(),
        answered_calls: "",
        un_ans_calls: "",
        num_of_email_sent: "",
        num_of_res: "",
        connection_linkedIn: "",
        sent_msg_linkedIn: "",
        respond_linkedIn: "",
      });
    }
  }, [editData]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    const {
      answered_calls,
      un_ans_calls,
      num_of_email_sent,
      num_of_res,
      connection_linkedIn,
      sent_msg_linkedIn,
      respond_linkedIn,
    } = formData;

    const answered = Number(answered_calls) || 0;
    const unanswered = Number(un_ans_calls) || 0;

    const payload = {
      date: formData.date.format("YYYY-MM-DD"),
      calls: {
        answered_calls: answered,
        un_ans_calls: unanswered,
      },
      emails: {
        num_of_email_sent: Number(num_of_email_sent) || 0,
      },
      responses: {
        num_of_res: Number(num_of_res) || 0,
      },
      linkedIn: {
        connection_linkedIn: Number(connection_linkedIn) || 0,
        sent_msg_linkedIn: Number(sent_msg_linkedIn) || 0,
        respond_linkedIn: Number(respond_linkedIn) || 0,
      },
    };

    if (editData?._id) {
      payload.id = editData._id;
    }

    const success = await onSave(payload);
    if (success && !editData) {
      setFormData({
        date: dayjs(),
        answered_calls: "",
        un_ans_calls: "",
        num_of_email_sent: "",
        num_of_res: "",
        connection_linkedIn: "",
        sent_msg_linkedIn: "",
        respond_linkedIn: "",
      });
    }
  };

  const SectionTitle = ({ title }) => (
    <Box className={classes.sectionTitle}>
      <TypographyInline
        size="xs"
        fontWeight={fontWeight.BOLD}
        color={GREY.SECONDARY}
        label={title.toUpperCase()}
      />
      <Divider style={{ flex: 1, borderColor: GREY.W_50 }} />
    </Box>
  );

  const Label = ({ text }) => (
    <Box className={classes.label}>
      <TypographyInline
        size="xs"
        fontWeight={fontWeight.MEDIUM}
        color={GREY.SECONDARY}
        label={text}
      />
    </Box>
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <TypographyInline
          size="md"
          fontWeight={fontWeight.SEMIBOLD}
          color={GREY.PRIMARY}
          label={
            editData
              ? `Edit Activity Log of date ${editData.date}`
              : "Add Activity Log"
          }
        />
      </Box>

      <Box className={classes.formContent}>
        <Box style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {/* Date */}
          <Box className={classes.field}>
            <Label text="Log Date" />
            <CustomDatePicker
              size="md40"
              value={formData.date}
              onChange={(date) => setFormData((prev) => ({ ...prev, date }))}
            />
          </Box>

          {/* Calls */}
          <SectionTitle title="Calls" />
          <Box className={classes.field}>
            <Label text="Answered Calls" />
            <InputField
              type="text"
              size="md40"
              placeholder="0"
              value={formData.answered_calls}
              onChange={handleChange("answered_calls")}
            />
          </Box>
          <Box className={classes.field}>
            <Label text="Un-answered Calls" />
            <InputField
              type="text"
              size="md40"
              placeholder="0"
              value={formData.un_ans_calls}
              onChange={handleChange("un_ans_calls")}
            />
          </Box>

          {/* Emails */}
          <SectionTitle title="Emails" />
          <Box className={classes.field}>
            <Label text="Emails Sent" />
            <InputField
              type="text"
              size="md40"
              placeholder="0"
              value={formData.num_of_email_sent}
              onChange={handleChange("num_of_email_sent")}
            />
          </Box>

          {/* Responses */}
          <SectionTitle title="Responses" />
          <Box className={classes.field}>
            <Label text="Responses Received" />
            <InputField
              type="text"
              size="md40"
              placeholder="0"
              value={formData.num_of_res}
              onChange={handleChange("num_of_res")}
            />
          </Box>

          {/* LinkedIn */}
          <SectionTitle title="LinkedIn" />
          <Box className={classes.field}>
            <Label text="LI Connects" />
            <InputField
              type="text"
              size="md40"
              placeholder="0"
              value={formData.connection_linkedIn}
              onChange={handleChange("connection_linkedIn")}
            />
          </Box>
          <Box className={classes.field}>
            <Label text="LI Messages Sent" />
            <InputField
              type="text"
              size="md40"
              placeholder="0"
              value={formData.sent_msg_linkedIn}
              onChange={handleChange("sent_msg_linkedIn")}
            />
          </Box>
          <Box className={classes.field}>
            <Label text="LI Replies Received" />
            <InputField
              type="text"
              size="md40"
              placeholder="0"
              value={formData.respond_linkedIn}
              onChange={handleChange("respond_linkedIn")}
            />
          </Box>
        </Box>
      </Box>

      <Box className={classes.footer} style={{ display: "flex", gap: "10px" }}>
        {editData && (
          <Button
            label="Cancel"
            variant="ghost"
            size="md40"
            fullWidth
            onClick={onCancel}
          />
        )}
        <Button
          label={editData ? "Update" : "Submit Activity Log"}
          variant="primary"
          size="md40"
          fullWidth
          onClick={handleSave}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};

export default ConsolidatedReportLog;
